import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

/** Admin control for auto-generated ULIDs (Unique LabTrack IDs). */
export const UlidSettingsCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ulid/settings").then(({ data }) => setCfg(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ulid/settings", cfg);
      setCfg(data);
      toast.success(data.enabled ? "Auto ULID generation is on" : "Auto ULID generation is off");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!cfg) return null;
  const d = new Date();
  const stamp = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const sample = `${cfg.prefix || "LTBP"}-${cfg.lab_code || "ABC"}-${stamp}0001`;

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="ulid-settings-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-sm text-[#111827]">Unique LabTrack ID (ULID)</h2>
          <p className="text-xs text-[#6B7280] mt-0.5 max-w-xl">
            Turn this on if the lab has no ID system of its own. LabTrack will then offer a
            generated ULID on every patient entry — prefix, lab code, the date written in reverse
            and a serial number that restarts each day.
          </p>
        </div>
        <Switch checked={!!cfg.enabled} disabled={!isAdmin}
          onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="ulid-enabled-switch" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mt-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Prefix</p>
          <Input value={cfg.prefix || ""} disabled={!isAdmin} maxLength={6}
            onChange={(e) => setCfg({ ...cfg, prefix: e.target.value.toUpperCase() })}
            className="mt-1 h-9" data-testid="ulid-prefix-input" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Lab code</p>
          <Input value={cfg.lab_code || ""} disabled={!isAdmin} maxLength={5}
            onChange={(e) => setCfg({ ...cfg, lab_code: e.target.value.toUpperCase() })}
            className="mt-1 h-9" data-testid="ulid-labcode-input" />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <p className="text-xs text-[#6B7280]">
          Next IDs will look like <span className="font-mono text-[#111827]"
            data-testid="ulid-sample">{sample}</span>
        </p>
        {isAdmin && (
          <Button size="sm" onClick={save} disabled={saving} data-testid="ulid-save-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save ULID settings"}
          </Button>
        )}
      </div>
    </div>
  );
};
